import { useId, useState, useMemo } from 'react';
import { ArrowLeft, Plus, Trophy, Info, Trash2, Tablet } from 'lucide-react';
import { getEloLevel, getPlayerKMultiplier, formatBoost, EngineSettings } from '../../engine';
import { MatchDetailModal, AddSingleMatchModal } from './';
import { PlayerPicker } from './PlayerPicker';
import { Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';

export interface SingleMatchesViewProps {
  /** All registered single matches (each with team1/team2/sets/result). */
  singleMatches: any[];
  /** Planned (not-yet-played) matches awaiting a result, linked to a booking. */
  plannedMatches?: any[];
  /** Court bookings — used to show date + court for each planned match. */
  bookings?: any[];
  /** Enters the result for a planned match; returns { success, error? }. */
  onCompletePlannedMatch?: (
    planId: string,
    data: any,
  ) => Promise<{ success: boolean; error?: string }>;
  /** Cancels (removes) a planned match without affecting ELO. */
  onCancelPlannedMatch?: (planId: string) => Promise<any> | void;
  /** Updates the four players on a planned match (booker editing before start). */
  onUpdatePlannedMatchPlayers?: (
    planId: string,
    teams: { team1: any; team2: any },
  ) => Promise<{ success: boolean; error?: string }>;
  /** All players available for lookup and the add/edit picker. */
  allPlayers: any[];
  /** Back navigation. */
  onBack: () => void;
  /** Persists a new match; returns { success, error? }. */
  onAddMatch: (data: any) => Promise<{ success: boolean; error?: string }>;
  /** Persists an edited match; returns { success, error? }. */
  onUpdateMatch: (id: string, data: any) => Promise<{ success: boolean; error?: string }>;
  /** Deletes a match (and reverts its ELO change). */
  onDeleteMatch: (id: string) => Promise<void> | void;
  /** Navigate to a player's profile. */
  onPlayerClick?: (p: any) => void;
  /** Optional: opens the enkeltkamp Banepad (per-court pads, like the KM/series banepad tab). */
  onOpenBanepad?: (() => void) | null;
  /** Optional: persists a brand-new player created inline in the add/edit modal. */
  onPersistNewPlayer?: ((player: any) => Promise<void> | void) | null;
  /**
   * Live engine wiring object (provided by the app shell). Passed straight through
   * to MatchDetailModal, which needs Padel24Engine.elo.K_FACTOR / .formatBoost
   * for its ELO-breakdown section. SingleMatchesView's own newbie-boost rendering
   * uses the named engine imports (getPlayerKMultiplier / formatBoost /
   * EngineSettings.NEWBIE_MATCH_THRESHOLD) instead of this object.
   */
  Padel24Engine: any;
}

export function SingleMatchesView({
  singleMatches,
  plannedMatches = [],
  bookings = [],
  onCompletePlannedMatch,
  onCancelPlannedMatch,
  onUpdatePlannedMatchPlayers,
  allPlayers,
  onBack,
  onAddMatch,
  onUpdateMatch,
  onDeleteMatch,
  onPlayerClick,
  onPersistNewPlayer = null,
  onOpenBanepad = null,
  Padel24Engine,
}: SingleMatchesViewProps) {
  // DEBUG: log props on every render so we can trace "matches disappearing"
  console.log(
    '[SingleMatchesView] render — singleMatches:',
    singleMatches.length,
    ', allPlayers:',
    allPlayers.length,
  );

  const [showAddModal, setShowAddModal] = useState(false);
  // Ids for the two `Dialog`s this view renders itself (#900).
  const uid = safeId('single-matches', useId());
  const dlg = {
    editTitle: `${uid}-edit-title`,
    editBody: `${uid}-edit-body`,
    deleteTitle: `${uid}-delete-title`,
    deleteBody: `${uid}-delete-body`,
  };
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [editingMatch, setEditingMatch] = useState(null);
  // The planned match currently being completed (result entry). null = none.
  const [completingPlan, setCompletingPlan] = useState<any>(null);
  // The planned match whose players are being edited (booker, before start). null = none.
  const [editingPlan, setEditingPlan] = useState<any>(null);
  const [editTeam1, setEditTeam1] = useState<{ p1Id: string | null; p2Id: string | null }>({
    p1Id: null,
    p2Id: null,
  });
  const [editTeam2, setEditTeam2] = useState<{ p1Id: string | null; p2Id: string | null }>({
    p1Id: null,
    p2Id: null,
  });
  const openEditPlayers = (plan: any) => {
    setEditTeam1({ p1Id: plan.team1?.p1Id ?? null, p2Id: plan.team1?.p2Id ?? null });
    setEditTeam2({ p1Id: plan.team2?.p1Id ?? null, p2Id: plan.team2?.p2Id ?? null });
    setEditingPlan(plan);
  };

  // Player lookup
  const playerById = useMemo(() => {
    const map = {};
    allPlayers.forEach((p) => {
      map[p.id] = p;
    });
    return map;
  }, [allPlayers]);

  const getName = (id) => playerById[id]?.name || `Spiller ${id}`;

  // Booking lookup — planned matches link to a booking (date/court/time).
  const bookingById = useMemo(() => {
    const map: Record<string, any> = {};
    (bookings || []).forEach((b) => {
      map[b.id] = b;
    });
    return map;
  }, [bookings]);
  // Local YYYY-MM-DD → "ons 8. jul" (NOT toISOString — local components).
  const shortDate = (iso?: string) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-').map(Number);
    if (!y || !m || !d) return '';
    const wd = ['søn', 'man', 'tir', 'ons', 'tor', 'fre', 'lør'][new Date(y, m - 1, d).getDay()];
    const mo = ['jan', 'feb', 'mar', 'apr', 'mai', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'des'][
      m - 1
    ];
    return `${wd} ${d}. ${mo}`;
  };
  const MATCH_FORMAT_LABEL: Record<string, string> = {
    bo3_super: 'Super-tiebreak',
    bo3_full: '3 fulle sett',
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 slide-up">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <button
            onClick={onBack}
            className="text-xs font-mono text-ink-3 hover:text-ink-2 mb-2 flex items-center gap-1"
          >
            <ArrowLeft size={12} /> Tilbake
          </button>
          <h1 className="font-display text-3xl font-semibold mb-1">Enkeltkamper</h1>
          <p className="text-sm text-ink-3">
            {singleMatches.length === 0
              ? 'Ingen kamper registrert ennå'
              : `${singleMatches.length} ${singleMatches.length === 1 ? 'kamp' : 'kamper'} registrert`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {onOpenBanepad && (
            <button onClick={onOpenBanepad} className="btn-ghost">
              <Tablet size={14} strokeWidth={2.5} /> Åpne banepad
            </button>
          )}
          <button onClick={() => setShowAddModal(true)} className="btn-lime">
            <Plus size={14} strokeWidth={2.5} /> Logg ny kamp
          </button>
        </div>
      </div>

      {/* Planlagte kamper — booked-but-not-yet-played matches awaiting a result */}
      {plannedMatches.length > 0 && (
        <div className="card overflow-hidden mb-6">
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5">
            <h2 className="font-mono text-[11px] font-semibold uppercase tracking-wider text-amber-800">
              Planlagte kamper · {plannedMatches.length}
            </h2>
            <p className="text-[11px] text-amber-700/80">
              Reservert via en banebooking — før inn resultatet når kampen er spilt.
            </p>
          </div>
          <div className="divide-y divide-line">
            {plannedMatches.map((plan) => {
              const training = plan.eloWeightPct === 0;
              const bk = bookingById[plan.bookingId];
              const when = bk
                ? `${shortDate(bk.date)} kl ${bk.startTime} · Bane ${bk.court}`
                : 'Dato/bane ikke funnet';
              const fmt = MATCH_FORMAT_LABEL[plan.matchFormat] || '';
              // The booker can change the players up until the match starts.
              const startMs =
                bk?.date && bk?.startTime
                  ? new Date(`${bk.date}T${bk.startTime}:00`).getTime()
                  : Infinity;
              const canEditPlayers = !!onUpdatePlannedMatchPlayers && Date.now() < startMs;
              return (
                <div
                  key={plan.id}
                  className="flex items-center justify-between gap-3 px-4 py-3 flex-wrap"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">
                      {getName(plan.team1?.p1Id)} / {getName(plan.team1?.p2Id)}
                      <span className="text-ink-3 mx-1.5">vs</span>
                      {getName(plan.team2?.p1Id)} / {getName(plan.team2?.p2Id)}
                    </div>
                    <div className="text-[11px] text-ink-2 mt-0.5 font-medium">{when}</div>
                    <div className="text-[11px] text-ink-3 mt-0.5">
                      {training
                        ? 'Treningskamp · teller ikke på ELO'
                        : `ELO-vekt ${plan.eloWeightPct}%`}
                      {fmt ? ` · ${fmt}` : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {canEditPlayers && (
                      <button onClick={() => openEditPlayers(plan)} className="btn-outline text-xs">
                        Endre spillere
                      </button>
                    )}
                    <button onClick={() => setCompletingPlan(plan)} className="btn-lime text-xs">
                      Registrer resultat
                    </button>
                    {onCancelPlannedMatch && (
                      <button
                        onClick={() => onCancelPlannedMatch(plan.id)}
                        className="btn-outline text-xs text-ink-3 hover:text-rose"
                      >
                        Avbryt
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Match list */}
      {singleMatches.length === 0 ? (
        <div className="card p-12 text-center text-ink-3">
          <Trophy size={32} className="mx-auto mb-3 opacity-50" />
          <div className="font-display text-lg mb-1">Ingen kamper registrert</div>
          <div className="text-sm mb-4">Logg din første enkeltkamp for å påvirke ELO</div>
          <button onClick={() => setShowAddModal(true)} className="btn-lime">
            <Plus size={14} strokeWidth={2.5} /> Logg ny kamp
          </button>
        </div>
      ) : (
        (() => {
          const gridCols = '80px 1fr 1fr 56px 44px 28px 44px 56px 1fr 1fr 32px';
          return (
            <div className="card overflow-hidden">
              {/* Header — two rows: home/away team label + column headers */}
              <div className="bg-ink text-white">
                {/* Group header with color tint */}
                <div
                  className="grid items-end gap-0.5 px-3 pt-2 pb-0 text-xs font-mono font-semibold uppercase tracking-wider"
                  style={{ gridTemplateColumns: gridCols }}
                >
                  <div></div>
                  {/* Home-team label with blue background */}
                  <div
                    className="text-center py-1.5 rounded-t-md text-white"
                    style={{
                      gridColumn: 'span 4 / span 4',
                      background: 'rgba(96, 165, 250, 0.20)',
                    }}
                  >
                    Hjemmelag
                  </div>
                  <div></div>
                  {/* Away-team label with orange background */}
                  <div
                    className="text-center py-1.5 rounded-t-md text-white"
                    style={{
                      gridColumn: 'span 4 / span 4',
                      background: 'rgba(251, 146, 60, 0.20)',
                    }}
                  >
                    Bortelag
                  </div>
                  <div></div>
                </div>
                {/* Column headers */}
                <div
                  className="grid items-center gap-0.5 px-3 pb-2 text-[11px] font-mono font-semibold uppercase tracking-wider"
                  style={{ gridTemplateColumns: gridCols }}
                >
                  <div className="px-1 py-1.5">Dato</div>
                  {/* Home-team columns — blue tint */}
                  <div
                    className="px-1 py-1.5 rounded-bl-md"
                    style={{ background: 'rgba(96, 165, 250, 0.12)' }}
                  >
                    Spiller 1
                  </div>
                  <div className="px-1 py-1.5" style={{ background: 'rgba(96, 165, 250, 0.12)' }}>
                    Spiller 2
                  </div>
                  <div
                    className="px-1 py-1.5 text-center"
                    style={{ background: 'rgba(96, 165, 250, 0.12)' }}
                  >
                    Lag-ELO
                  </div>
                  <div
                    className="px-1 py-1.5 text-center rounded-br-md"
                    style={{ background: 'rgba(96, 165, 250, 0.12)' }}
                  >
                    Score
                  </div>
                  <div className="px-1 py-1.5 text-center text-white/40">vs</div>
                  {/* Away-team columns — orange tint */}
                  <div
                    className="px-1 py-1.5 text-center rounded-bl-md"
                    style={{ background: 'rgba(251, 146, 60, 0.12)' }}
                  >
                    Score
                  </div>
                  <div
                    className="px-1 py-1.5 text-center"
                    style={{ background: 'rgba(251, 146, 60, 0.12)' }}
                  >
                    Lag-ELO
                  </div>
                  <div className="px-1 py-1.5" style={{ background: 'rgba(251, 146, 60, 0.12)' }}>
                    Spiller 1
                  </div>
                  <div
                    className="px-1 py-1.5 rounded-br-md"
                    style={{ background: 'rgba(251, 146, 60, 0.12)' }}
                  >
                    Spiller 2
                  </div>
                  <div></div>
                </div>
              </div>

              {/* Rows */}
              {singleMatches.map((m, idx) => {
                // Defensive checks — skip the match if it is missing critical fields
                if (!m || !m.team1 || !m.team2 || !Array.isArray(m.sets)) {
                  console.warn('[SingleMatchesView] Hopper over ugyldig kamp:', m);
                  return null;
                }

                const date = new Date(m.playedAt || Date.now());
                const dateLabel = date.toLocaleDateString('no-NO', {
                  day: '2-digit',
                  month: 'short',
                });
                const timeLabel = date.toLocaleTimeString('no-NO', {
                  hour: '2-digit',
                  minute: '2-digit',
                });
                const t1Won = m.result?.winner === 'team1';
                const isAlt = idx % 2 === 1;

                const t1SetWins = m.sets.filter((s) => s && s.games1 > s.games2).length;
                const t2SetWins = m.sets.filter((s) => s && s.games2 > s.games1).length;

                const t1p1 = playerById[m.team1.p1Id];
                const t1p2 = playerById[m.team1.p2Id];
                const t2p1 = playerById[m.team2.p1Id];
                const t2p2 = playerById[m.team2.p2Id];

                // Team ELO before the match + change
                const t1TeamElo =
                  m.result?.t1EloBefore != null ? Math.round(m.result.t1EloBefore) : null;
                const t2TeamElo =
                  m.result?.t2EloBefore != null ? Math.round(m.result.t2EloBefore) : null;
                const t1Change = m.result?.t1Change ?? 0;
                const t2Change = m.result?.t2Change ?? 0;
                // Newbie multipliers from the result — used to show the base numbers in the team pill
                const t1MatchNewbieMul = m.result?.t1NewbieMul ?? 1.0;
                const t2MatchNewbieMul = m.result?.t2NewbieMul ?? 1.0;
                const t1UnboostedChange =
                  t1MatchNewbieMul > 0 ? Math.round(t1Change / t1MatchNewbieMul) : t1Change;
                const t2UnboostedChange =
                  t2MatchNewbieMul > 0 ? Math.round(t2Change / t2MatchNewbieMul) : t2Change;

                // Helper: render one player cell (with ELO-level pill + name + ELO + newbie badge)
                // displayElo: the player's ELO at the time of this match
                // isT1: which team the player is on (to show the correct actual ELO change behind the 2x pill)
                const renderPlayer = (p, isWinner, displayElo, isT1Side) => {
                  if (!p) return <div className="text-xs text-ink-3 italic">—</div>;
                  const eloToShow = displayElo != null ? displayElo : p.elo;
                  // Get the ELO level (1-9) based on the player's ELO at that time
                  const level = getEloLevel(eloToShow);

                  // Check whether the player currently has a newbie boost
                  const newbieMul = getPlayerKMultiplier(p);
                  const isBoosted = newbieMul > 1;
                  const teamChange = isT1Side ? t1Change : t2Change;

                  return (
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span
                        className="font-mono text-[9px] font-bold px-1 rounded flex-shrink-0"
                        style={{ background: level.color, color: level.text }}
                        title={`Nivå ${level.level} — ${level.name}`}
                      >
                        {level.level}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onPlayerClick?.(p);
                        }}
                        className={`text-xs truncate text-left hover:underline ${isWinner ? 'font-bold text-ink' : ''}`}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          padding: 0,
                          cursor: 'pointer',
                          minWidth: 0,
                        }}
                        title={`Vis profil for ${p.name}`}
                      >
                        {p.name}
                      </button>
                      {isBoosted && (
                        <span
                          className="font-mono text-[9px] font-bold px-1 rounded flex-shrink-0 inline-flex items-center gap-1"
                          style={{ background: '#fef3c7', color: '#92400e' }}
                          title={`Ny spiller-boost ${formatBoost(newbieMul)} — avtar lineært over kamper, blir 1x når spilleren har spilt ${EngineSettings.NEWBIE_MATCH_THRESHOLD} kamper.`}
                        >
                          <span>{formatBoost(newbieMul)}</span>
                          {teamChange != null && (
                            <span
                              style={{
                                color:
                                  teamChange > 0
                                    ? '#3f6212'
                                    : teamChange < 0
                                      ? '#9f1239'
                                      : '#92400e',
                              }}
                            >
                              {teamChange > 0 ? '+' : ''}
                              {teamChange}
                            </span>
                          )}
                        </span>
                      )}
                      <span className="text-[10px] font-mono text-ink-3 flex-shrink-0">
                        {eloToShow}
                      </span>
                    </div>
                  );
                };

                return (
                  <div
                    key={m.id}
                    className={`grid items-stretch gap-0.5 px-3 py-2 text-sm hover:bg-bg-soft/40 border-b border-line/40 last:border-b-0 ${isAlt ? 'bg-bg-soft/20' : ''}`}
                    style={{ gridTemplateColumns: gridCols }}
                  >
                    {/* Date */}
                    <div className="text-[11px] font-mono leading-tight self-center">
                      <div>{dateLabel}</div>
                      <div className="text-ink-3">{timeLabel}</div>
                    </div>

                    {/* Home player 1 */}
                    <div className="self-center">
                      {renderPlayer(t1p1, t1Won, m.result?.playerEloPre?.[t1p1?.id], true)}
                    </div>

                    {/* Home player 2 */}
                    <div className="self-center">
                      {renderPlayer(t1p2, t1Won, m.result?.playerEloPre?.[t1p2?.id], true)}
                    </div>

                    {/* Home team ELO — base (without newbie boost) */}
                    <div className="text-center self-center">
                      <div
                        className={`font-mono text-xs font-semibold ${
                          t1TeamElo != null && t2TeamElo != null && t1TeamElo > t2TeamElo
                            ? 'text-lime-deep'
                            : t1TeamElo === t2TeamElo
                              ? 'text-ink-2'
                              : 'text-ink-3'
                        }`}
                      >
                        {t1TeamElo ?? '–'}
                      </div>
                      <div
                        className={`font-mono text-[11px] font-semibold ${t1UnboostedChange >= 0 ? 'text-lime-deep' : 'text-rose'}`}
                      >
                        {t1UnboostedChange >= 0 ? '+' : ''}
                        {t1UnboostedChange}
                      </div>
                    </div>

                    {/* Home score — sets won */}
                    <div
                      className={`text-center self-center font-mono font-bold text-base ${t1Won ? 'text-lime-deep' : 'text-ink-3'}`}
                    >
                      {t1SetWins}
                    </div>

                    {/* vs */}
                    <div className="text-center self-center text-ink-3 text-xs">vs</div>

                    {/* Away score */}
                    <div
                      className={`text-center self-center font-mono font-bold text-base ${!t1Won ? 'text-lime-deep' : 'text-ink-3'}`}
                    >
                      {t2SetWins}
                    </div>

                    {/* Away team ELO — base (without newbie boost) */}
                    <div className="text-center self-center">
                      <div
                        className={`font-mono text-xs font-semibold ${
                          t1TeamElo != null && t2TeamElo != null && t2TeamElo > t1TeamElo
                            ? 'text-lime-deep'
                            : t1TeamElo === t2TeamElo
                              ? 'text-ink-2'
                              : 'text-ink-3'
                        }`}
                      >
                        {t2TeamElo ?? '–'}
                      </div>
                      <div
                        className={`font-mono text-[11px] font-semibold ${t2UnboostedChange >= 0 ? 'text-lime-deep' : 'text-rose'}`}
                      >
                        {t2UnboostedChange >= 0 ? '+' : ''}
                        {t2UnboostedChange}
                      </div>
                    </div>

                    {/* Away player 1 */}
                    <div className="self-center">
                      {renderPlayer(t2p1, !t1Won, m.result?.playerEloPre?.[t2p1?.id], false)}
                    </div>

                    {/* Away player 2 */}
                    <div className="self-center">
                      {renderPlayer(t2p2, !t1Won, m.result?.playerEloPre?.[t2p2?.id], false)}
                    </div>

                    {/* Detail button */}
                    <div className="self-center flex justify-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedMatch(m);
                        }}
                        className="p-1.5 rounded-md text-ink-3 hover:bg-bg-soft hover:text-ink-2 transition-colors"
                        title="Vis kampdetaljer"
                      >
                        <Info size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* Footnote */}
              <div className="px-3 py-2 bg-bg-soft/30 text-[11px] text-ink-3 border-t border-line">
                Klikk på <Info size={10} className="inline mb-0.5" /> for å se kampdetaljer · Klikk
                på navn for spillerprofil · Score = antall sett vunnet
              </div>
            </div>
          );
        })()
      )}

      {/* Detail modal */}
      {selectedMatch && (
        <MatchDetailModal
          match={selectedMatch}
          getName={getName}
          playerById={playerById}
          onClose={() => setSelectedMatch(null)}
          onDelete={() => {
            setConfirmDelete(selectedMatch);
            setSelectedMatch(null);
          }}
          onEdit={() => {
            setEditingMatch(selectedMatch);
            setSelectedMatch(null);
          }}
          onPlayerClick={onPlayerClick}
          Padel24Engine={Padel24Engine}
        />
      )}

      {/* Edit modal — reuses AddSingleMatchModal with initial data */}
      {editingMatch && (
        <AddSingleMatchModal
          allPlayers={allPlayers}
          initialMatch={editingMatch}
          onPersistNewPlayer={onPersistNewPlayer}
          onClose={() => setEditingMatch(null)}
          onSubmit={async (data) => {
            const result = await onUpdateMatch(editingMatch.id, data);
            if (result.success) {
              setEditingMatch(null);
            } else {
              alert('Feil: ' + result.error);
            }
          }}
        />
      )}

      {/* Add modal */}
      {showAddModal && (
        <AddSingleMatchModal
          allPlayers={allPlayers}
          onPersistNewPlayer={onPersistNewPlayer}
          onClose={() => setShowAddModal(false)}
          onSubmit={async (data) => {
            const result = await onAddMatch(data);
            if (result.success) {
              setShowAddModal(false);
            } else {
              alert('Feil: ' + result.error);
            }
          }}
        />
      )}

      {/* Complete-a-planned-match modal — reuses AddSingleMatchModal pre-seeded
          with the plan's players + ELO weight; the user just enters the score. */}
      {completingPlan && onCompletePlannedMatch && (
        <AddSingleMatchModal
          allPlayers={allPlayers}
          initialMatch={{
            team1: completingPlan.team1,
            team2: completingPlan.team2,
            eloWeightPct: completingPlan.eloWeightPct,
            sets: [],
            playedAt: completingPlan.createdAt || Date.now(),
          }}
          title="Registrer resultat"
          submitLabel="Registrer resultat"
          onPersistNewPlayer={onPersistNewPlayer}
          onClose={() => setCompletingPlan(null)}
          onSubmit={async (data) => {
            const result = await onCompletePlannedMatch(completingPlan.id, data);
            if (result.success) {
              setCompletingPlan(null);
            } else {
              alert('Feil: ' + result.error);
            }
          }}
        />
      )}

      {/* Edit-players modal — the booker changes the four players before start. */}
      {editingPlan &&
        onUpdatePlannedMatchPlayers &&
        (() => {
          const ids = [editTeam1.p1Id, editTeam1.p2Id, editTeam2.p1Id, editTeam2.p2Id];
          const allSet = ids.every(Boolean) && new Set(ids).size === 4;
          return (
            // A `Dialog` (#900): named by its title, described by the line under
            // it. The player picker opens its own Dialog inside it, which is the
            // innermost while it is up, so Escape there closes the picker only.
            <Dialog
              labelledBy={dlg.editTitle}
              describedBy={dlg.editBody}
              onClose={() => setEditingPlan(null)}
              showClose={false}
              backdropClassName="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
              className="card p-5 max-w-md w-full max-h-[90vh] overflow-y-auto"
            >
              <h2 id={dlg.editTitle} className="font-display text-lg font-semibold mb-1">
                Endre spillere
              </h2>
              <p id={dlg.editBody} className="text-xs text-ink-3 mb-4">
                Bytt ut spillere fram til kampen starter.
              </p>
              <PlayerPicker
                allPlayers={allPlayers}
                team1={editTeam1}
                team2={editTeam2}
                onChangeTeam1={setEditTeam1}
                onChangeTeam2={setEditTeam2}
                onPersistNewPlayer={onPersistNewPlayer}
              />
              {!allSet && (
                <p className="text-[11px] text-amber-700 mt-3">
                  Velg fire ulike spillere for å lagre.
                </p>
              )}
              <div className="flex gap-2 justify-end mt-5 pt-4 border-t border-line">
                <button
                  type="button"
                  onClick={() => setEditingPlan(null)}
                  className="btn-outline text-sm"
                >
                  Avbryt
                </button>
                <button
                  type="button"
                  disabled={!allSet}
                  onClick={async () => {
                    const result = await onUpdatePlannedMatchPlayers(editingPlan.id, {
                      team1: editTeam1,
                      team2: editTeam2,
                    });
                    if (result?.success) setEditingPlan(null);
                    else alert('Feil: ' + (result?.error || 'ukjent feil'));
                  }}
                  className="btn-lime text-sm disabled:opacity-40"
                >
                  Lagre
                </button>
              </div>
            </Dialog>
          );
        })()}

      {/* Confirm deletion */}
      {confirmDelete && (
        // A `Dialog` (#900). Escape is its «Avbryt» — nothing is deleted — and
        // focus starts there. It takes over from the match details (whose
        // «Slett kamp» opened it), so on close focus goes back to the match row
        // that opened the details.
        <Dialog
          labelledBy={dlg.deleteTitle}
          describedBy={dlg.deleteBody}
          onClose={() => setConfirmDelete(null)}
          showClose={false}
          backdropClassName="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
          className="card p-6 max-w-sm w-full overflow-y-auto max-h-[92vh]"
          style={{ maxHeight: 'calc(100dvh - 2rem)' }}
        >
          <h2 id={dlg.deleteTitle} className="font-display text-lg font-semibold mb-2">
            Slett enkeltkamp?
          </h2>
          <p id={dlg.deleteBody} className="text-sm text-ink-2 mb-4">
            ELO-endringen blir reversert, og spillernes ELO går tilbake til verdiene før kampen.
          </p>
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => setConfirmDelete(null)}
              className="btn-outline text-sm"
            >
              Avbryt
            </button>
            <button
              type="button"
              onClick={async () => {
                await onDeleteMatch(confirmDelete.id);
                setConfirmDelete(null);
              }}
              className="btn-danger text-sm"
            >
              <Trash2 size={12} /> Slett
            </button>
          </div>
        </Dialog>
      )}
    </div>
  );
}
